import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Package, Tag, DollarSign, ArrowLeft,
  MapPin, Box, CheckCircle, History, Info,
  Scissors, Settings, Layers, Star, TrendingUp,
  AlertCircle, ShoppingBag, Archive, Percent
} from "lucide-react";
import { getProductById } from "../../../services/productsApi";
import { Product } from "../../../types/product";
import GlobalLoader from "../../../components/ui/GlobalLoader";
import PageHeader from "../../../components/ui/PageHeader";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

export default function ProductDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await getProductById(id);
        if (data) {
          setProduct(data);
        } else {
          setError("Product not found.");
        }
      } catch (err) {
        console.error("Failed to load product details", err);
        setError("Could not load product details.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const chartData = useMemo(() => {
    const months = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    // Simulate sales velocity
    return months.map((m, i) => ({
      name: m,
      value: 5 + Math.floor(Math.random() * 20) + (i * 3)
    }));
  }, []);

  if (loading) {
    return (
      <div className="flex h-[80vh] w-full items-center justify-center bg-gray-50">
        <GlobalLoader description="Loading inventory details..." />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50 text-center p-6">
        <Package className="w-12 h-12 text-[#885ABB]/50 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Item Not Found</h2>
        <p className="text-gray-500 mb-6">{error || "The requested product details could not be found."}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-[#885ABB] text-white rounded-full hover:bg-[#724b9e] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const formatCurrency = (amount: number | string) => {
    const val = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(val || 0);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <PageHeader
        title="Product Details"
        icon={Package}
        className="border-b-2 border-gray-300 shadow-sm"
        actions={
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">

        {/* Hero Section */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50"></div>

          <div className="relative flex flex-col md:flex-row items-center md:items-start gap-8">
            <div className="relative">
              <div className="w-32 h-32 rounded-full bg-[#885ABB]/10 border-4 border-white shadow-md flex items-center justify-center overflow-hidden">
                {product.image && !product.image.includes('default') && !product.image.includes('placeholder') ? (
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-16 h-16 text-[#885ABB]/70" />
                )}
              </div>
              <div className="absolute bottom-1 right-1 group">
                <div className={`w-6 h-6 border-4 border-white rounded-full ${product.status === 'active' || product.status === '1' ? 'bg-green-500' : 'bg-gray-300'} transition-colors cursor-help`}></div>

                {/* Status Tooltip */}
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-lg z-10">
                  {product.status === 'active' || product.status === '1' ? 'Status: Active' : 'Status: Inactive'}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>

            <div className="flex-1 text-center md:text-left space-y-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center md:justify-start gap-3">
                  {product.name}
                  {(product.status === 'active' || product.status === '1') && (
                    <CheckCircle className="w-5 h-5 text-[#885ABB]" fill="currentColor" stroke="white" />
                  )}
                </h1>
                <p className="text-gray-500 flex items-center justify-center md:justify-start gap-2 mt-1 lowercase first-letter:uppercase">
                  <Tag className="w-4 h-4" />
                  SKU: {product.sku || "N/A"}
                </p>
              </div>

              <div className="flex flex-wrap justify-center md:justify-start gap-2">
                {product.discount && parseFloat(product.discount.toString()) > 0 && (
                  <span className="px-3 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase border border-red-100 flex items-center gap-1">
                    <Percent className="w-3 h-3" /> {product.discount}% Discount
                  </span>
                )}
                {product.stock !== undefined && product.stock <= 5 && (
                  <span className="px-3 py-1 rounded-full bg-orange-50 text-orange-600 text-[10px] font-bold uppercase border border-orange-100 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Low Stock
                  </span>
                )}
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full md:w-auto">
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 text-center min-w-[100px]">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Retail Price</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{product.formattedPrice || formatCurrency(product.price || 0)}</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB] uppercase tracking-wide">In Stock</p>
                <p className="text-xl font-bold text-[#885ABB] mt-1">{product.stock ?? product.quantity ?? 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50/70 border border-purple-100/70 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB]/80 uppercase tracking-wide">Value</p>
                <p className="text-lg font-bold text-[#885ABB] mt-1">
                  {product.formattedPrice
                    ? product.formattedPrice.replace(/[\d.,]+/, ((product.stock || 0) * (typeof product.price === 'string' ? parseFloat(product.price) : (product.price || 0))).toFixed(2))
                    : formatCurrency((product.stock || 0) * (typeof product.price === 'string' ? parseFloat(product.price) : (product.price || 0)))}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50/40 border border-purple-100/40 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB]/60 uppercase tracking-wide">Status</p>
                <p className="text-lg font-bold text-[#885ABB] mt-1 uppercase">{(product.status === 'active' || product.status === '1') ? 'Active' : 'Inactive'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left Column */}
          <div className="space-y-6">
            {/* Trend Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6">
              <h3 className="font-semibold text-[#885ABB] mb-6 flex items-center gap-2">
                <TrendingUp size={18} /> Sales Velocity
              </h3>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#885ABB" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#885ABB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                    <YAxis hide />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="value" stroke="#885ABB" fillOpacity={1} fill="url(#colorSales)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Inventory Details */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-purple-200 bg-white">
                <h3 className="font-semibold text-[#885ABB]">Identification</h3>
              </div>
              <div className="p-6 space-y-4">


                <div className="flex items-start gap-4">
                  <Archive className="w-5 h-5 text-[#885ABB] shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500">Stock Keeping Unit (SKU)</p>
                    <p className="text-sm text-gray-900 font-bold mt-1">{product.sku || "N/A"}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-[#885ABB] shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500">Assigned Venue</p>
                    <p className="text-sm text-gray-900 font-bold mt-1">{product.locationName || "Default Venue"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Taxes Card */}
            {product.taxes && product.taxes.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b-2 border-purple-200 bg-white">
                  <h3 className="font-semibold text-[#885ABB]">Applicable Taxes</h3>
                </div>
                <div className="p-6 space-y-3">
                  {product.taxes.map((tax, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-2">
                        <Percent size={14} className="text-[#885ABB]" />
                        <span className="text-sm font-medium text-gray-700">{tax.name}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{tax.percent}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Description & Specs */}
          <div className="lg:col-span-2 space-y-6">
            <div className="border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b-2 border-[#885ABB] inline-block">Product Overview</h3>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-8 space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Description</h4>
                <p className="text-gray-700 leading-relaxed italic">
                  "{product.description || "No detailed description available for this product."}"
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-gray-100">
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Pricing Details</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <span className="text-gray-500 text-sm">Retail Price:</span>
                      <span className="font-bold text-lg">{product.formattedPrice || formatCurrency(product.price || 0)}</span>
                    </div>
                    {product.discountedPrice !== undefined && product.discountedPrice < (typeof product.price === 'string' ? parseFloat(product.price) : product.price) && (
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-100">
                        <span className="text-green-600 text-sm font-medium">Limited Time Offer:</span>
                        <span className="font-bold text-green-700 text-lg">{product.formattedDiscountedPrice || formatCurrency(product.discountedPrice)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Inventory Status</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <span className="text-gray-500 text-sm">Current Stock:</span>
                      <span className="font-bold">{product.stock ?? product.quantity ?? 0} units</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <span className="text-gray-500 text-sm">Product SKU:</span>
                      <span className="text-gray-400 font-mono text-xs">{product.sku || "N/A"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* More Details section based on JSON */}
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Organizational Placement</h4>
              <div className="flex items-center gap-6">
                <div className="flex-1 p-4 rounded-xl bg-purple-50 border border-purple-100">
                  <p className="text-xs text-purple-400 uppercase font-bold tracking-widest mb-1">Assigned Branch</p>
                  <p className="text-lg font-bold text-[#885ABB]">{product.locationName || "Global Inventory"}</p>
                </div>
                {product.category && (
                  <div className="flex-1 p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="text-xs text-gray-400 uppercase font-bold tracking-widest mb-1">Product Category</p>
                    <p className="text-lg font-bold text-gray-700">{product.category}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
